import type React from "react";
import type { Comment } from "@/types/channel/comment";
import { useAuth } from "@/hooks/useAuth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { formatDistance } from "date-fns";
import { ja } from "date-fns/locale";
import { MoreHorizontal, Heart } from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { useUpdateComment } from "@/hooks/channel/useUpdateComment";
import { useDeleteComment } from "@/hooks/channel/useDeleteComment";
import { useToast } from "@/hooks/ui/use-toast";

interface CommentItemProps {
	comment: Comment;
}

export const CommentItem: React.FC<CommentItemProps> = ({ comment }) => {
	const { user } = useAuth();
	const { toast } = useToast();
	const [isEditing, setIsEditing] = useState(false);
	const [editContent, setEditContent] = useState(comment.content);
	const { updateComment, loading: updating } = useUpdateComment();
	const { deleteComment, loading: deleting } = useDeleteComment();

	const handleUpdate = async () => {
		if (!editContent.trim()) return;

		try {
			await updateComment({
				commentId: comment.id,
				content: editContent.trim(),
			});
			setIsEditing(false);
			toast({
				title: "コメントを更新しました",
				variant: "default",
			});
		} catch (error) {
			toast({
				title: "コメントの更新に失敗しました",
				variant: "destructive",
			});
		}
	};

	const handleDelete = async () => {
		try {
			await deleteComment(comment.id);
			toast({
				title: "コメントを削除しました",
				variant: "default",
			});
		} catch (error) {
			toast({
				title: "コメントの削除に失敗しました",
				variant: "destructive",
			});
		}
	};

	return (
		<div className="bg-gray-50 rounded-lg p-4">
			<div className="flex justify-between">
				<div className="flex space-x-3">
					<Avatar className="h-8 w-8">
						<AvatarImage src={comment.user?.avatarUrl} />
						<AvatarFallback>{comment.user?.username?.[0]}</AvatarFallback>
					</Avatar>
					<div>
						<p className="font-semibold text-sm">{comment.user?.username}</p>
						<p className="text-xs text-gray-500">
							{formatDistance(new Date(comment.createdAt), new Date(), {
								addSuffix: true,
								locale: ja,
							})}
						</p>
					</div>
				</div>

				{user?.id === comment.userId && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" size="sm">
								<MoreHorizontal className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem onClick={() => setIsEditing(true)}>
								編集
							</DropdownMenuItem>
							<DropdownMenuItem
								className="text-red-600"
								onClick={handleDelete}
								disabled={deleting}
							>
								削除
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				)}
			</div>

			{isEditing ? (
				<div className="mt-2 space-y-2">
					<Textarea
						value={editContent}
						onChange={(e) => setEditContent(e.target.value)}
						className="min-h-[60px]"
					/>
					<div className="flex justify-end space-x-2">
						<Button
							size="sm"
							variant="outline"
							onClick={() => setIsEditing(false)}
						>
							キャンセル
						</Button>
						<Button
							size="sm"
							onClick={handleUpdate}
							disabled={updating || !editContent.trim()}
						>
							更新
						</Button>
					</div>
				</div>
			) : (
				<>
					<p className="mt-2 text-sm">{comment.content}</p>
					<div className="mt-2">
						<Button variant="ghost" size="sm">
							<Heart className="h-3 w-3 mr-1" />
							{comment.reactions?.length || 0}
						</Button>
					</div>
				</>
			)}
		</div>
	);
};
